/**
 * True Cost to Funded — Calculator
 * ==================================
 * Computes: Eval Fee (discounted) + Activation Fee + 1 Reset
 *
 * Confidence levels:
 *   "exact"     — all 3 components are known numeric values
 *   "estimated" — at least one component uses a midpoint or approximation
 *   "partial"   — reset fee unknown, showing eval + activation only
 */

import { parsePrice, parseActivationFee, parseResetFee, computeDiscountedPrice } from "./priceUtils";

/**
 * @param {Object} firm — full firm object from API
 * @param {Object} pricingItem — the specific pricing tier (e.g., 50K)
 * @returns {Object} trueCost breakdown
 */
export const computeTrueCost = (firm, pricingItem) => {
  if (!firm || !pricingItem) {
    return null;
  }

  const sizeLabel = pricingItem.size || "";
  const firmSizes = (firm.pricing || []).map(p => p.size);

  // 1. Eval fee (apply discount if active)
  const baseEval = parsePrice(pricingItem.price);
  const discountPct = firm.discount_percent || 0;
  const isDiscountLive = firm.is_discount_live === true;
  const evalFee = (isDiscountLive && discountPct > 0)
    ? (computeDiscountedPrice(baseEval, discountPct) || baseEval)
    : baseEval;

  // 2. Activation fee
  const afParsed = parseActivationFee(firm.activation_fee, sizeLabel);
  let activationFee = 0;
  let activationDisplay = "$0";
  let activationNote = "No activation fee";

  if (afParsed.feeForSize) {
    const val = parseFloat(String(afParsed.feeForSize).replace(/[^0-9.]/g, ""));
    if (!isNaN(val) && val > 0) {
      activationFee = val;
      activationDisplay = `$${val}`;
      activationNote = afParsed.isPerSize ? `For ${sizeLabel}` : null;
    }
  } else if (afParsed.rawString) {
    // Complex string — try to extract the best dollar amount
    const raw = afParsed.rawString;

    // Prefer one-time fee over monthly when "or" splits exist
    let source = raw;
    if (/\bor\b/i.test(raw)) {
      const parts = raw.split(/\bor\b/i);
      const oneTimePart = parts.find(p => /one.?time/i.test(p));
      if (oneTimePart) source = oneTimePart;
    }

    // Handle range "$X-$Y" → use midpoint
    const rangeMatch = source.match(/\$([\d,.]+)\s*[-–]\s*\$?([\d,.]+)/);
    if (rangeMatch) {
      const low = parseFloat(rangeMatch[1].replace(/,/g, ""));
      const high = parseFloat(rangeMatch[2].replace(/,/g, ""));
      activationFee = Math.round((low + high) / 2);
      activationDisplay = `~$${activationFee}`;
      activationNote = `Estimated from "${raw}"`;
    } else {
      // Fallback: first dollar amount in the source segment
      const match = source.match(/\$(\d[\d,.]*)/);
      if (match) {
        activationFee = parseFloat(match[1].replace(/,/g, ""));
        activationDisplay = `~$${activationFee}`;
        activationNote = `Estimated from "${raw}"`;
      }
    }
  }

  // 3. Reset fee — prefer per-size reset_fee from pricing item, fallback to firm-level
  const resetFeeSource = pricingItem.reset_fee || firm.reset_fee;
  const rfParsed = parseResetFee(resetFeeSource, sizeLabel, firmSizes);

  // 4. Compute total and confidence
  let totalCost = evalFee + activationFee;
  let confidence = "exact";
  let resetIncluded = false;

  if (rfParsed.fee != null) {
    totalCost += rfParsed.fee;
    resetIncluded = true;
    if (rfParsed.confidence === "estimated") {
      confidence = "estimated";
    }
  } else {
    // Reset unknown — partial confidence
    confidence = "partial";
  }

  // If activation was estimated, downgrade confidence
  if (activationDisplay.startsWith("~")) {
    confidence = confidence === "partial" ? "partial" : "estimated";
  }

  return {
    totalCost,
    evalFee,
    activationFee,
    resetFee: rfParsed.fee,
    resetIncluded,
    confidence,
    display: confidence === "partial" ? `$${totalCost}+` : (confidence === "estimated" ? `~$${totalCost}` : `$${totalCost}`),
    components: {
      eval: {
        amount: evalFee,
        display: `$${evalFee}`,
        label: isDiscountLive && discountPct > 0 ? `Eval (${discountPct}% off)` : "Eval fee",
      },
      activation: {
        amount: activationFee,
        display: activationDisplay,
        label: "Activation fee",
        note: activationNote,
      },
      reset: {
        amount: rfParsed.fee,
        display: rfParsed.display || "Unknown",
        label: "1 reset",
        note: rfParsed.note,
        confidence: rfParsed.confidence,
      },
    },
  };
};

/**
 * Format true cost for compact table display.
 * @returns {{ text: string, tooltip: string, confidence: string }}
 */
export const formatTrueCostCompact = (trueCost) => {
  if (!trueCost) return { text: "—", tooltip: "", confidence: "unknown" };

  const { components: c, confidence } = trueCost;
  const parts = [`Eval: ${c.eval.display}`];
  if (c.activation.amount > 0) parts.push(`Activation: ${c.activation.display}`);
  if (c.reset.amount != null) {
    parts.push(`Reset: ${c.reset.display}${c.reset.note ? ` (${c.reset.note})` : ""}`);
  } else {
    parts.push("Reset: not included (unknown)");
  }

  return {
    text: trueCost.display,
    tooltip: parts.join(" + "),
    confidence,
  };
};
